import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root',
})
export class DataTransformHelper {
    /**
     * @description Maneja valores vacios o nulos, devolviendo un guion ('-') en caso de que el valor sea falsy o una cadena vacia, y devolviendo el valor original en caso contrario.
     * @param value El valor a evaluar, que puede ser de cualquier tipo.
     * @returns El valor original si es truthy y no es una cadena vacia, o un guion ('-') si el valor es falsy o una cadena vacia.
     */
    handleEmpty(value: any) {
        if (!value || value === '') {
            return '-';
        }
        return value;
    }

    /**
     * @description Construye campos de GraphQL de manera dinamica basandose en una lista de campos con notacion de punto, generando una estructura anidada que representa la jerarquia de los campos para su uso en consultas GraphQL.
     * @param fields Array de strings con notacion de punto (ej: ['id', 'name', 'address.city', 'address.country'])
     * @returns String formateado para usar en queries GraphQL, representando la estructura anidada de los campos.
     */
    buildGraphQLFields = (fields: string[]): string => {
        const tree: any = {};

        for (const field of fields) {
            const parts = field.split('.');
            let current = tree;

            for (let i = 0; i < parts.length; i++) {
                const part = parts[i];
                if (!current[part]) {
                    current[part] = {};
                }
                current = current[part];
            }
        }

        const build = (node: Record<string, any>, indent = 2): string => {
            return Object.entries(node)
                .map(([key, value]) => {
                    if (Object.keys(value).length === 0) {
                        return ' '.repeat(indent) + key;
                    }
                    return (
                        ' '.repeat(indent) +
                        key +
                        ' {\n' +
                        build(value, indent + 2) +
                        '\n' +
                        ' '.repeat(indent) +
                        '}'
                    );
                })
                .join('\n');
        };
        return build(tree);
    };

    /**
     * @description Aplana un objeto anidado extrayendo todos los valores de tipo string, numero o booleano, convirtiendolos a minusculas y devolviendo un array con estos valores. Es util para realizar busquedas o filtrados basados en cualquier valor dentro de una estructura de datos compleja.
     * @param obj El objeto a aplanar, que puede contener propiedades anidadas de cualquier nivel.
     * @returns Un array de strings que contiene todos los valores extraidos del objeto, convertidos a minusculas.
     */
    flattenStrings(obj: any): string[] {
        const result: string[] = [];

        const flattenValues = (value: any) => {
            if (value == null) return;
            if (typeof value === 'string') {
                result.push(value.toLowerCase());
            } else if (typeof value === 'number' || typeof value === 'boolean') {
                result.push(value.toString().toLowerCase());
            } else if (Array.isArray(value)) {
                value.forEach(flattenValues);
            } else if (typeof value === 'object') {
                Object.values(value).forEach(flattenValues);
            }
        };

        flattenValues(obj);
        return result;
    }
}
